// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// Admin API client for living profile-form question-set schemas (fact-find,
// risk attitude, …), owned by the Resource Server. Platform admins edit the
// global default; brokerage admins edit a per-brokerage override (scoped by
// X-Brokerage-Id). The admin-bff proxies /api/** to the resource-server, so no
// BFF route is needed. See docs/specs/repeatable-profile-forms-prd.md.

/** The form types backed by the versioned profile-form store. */
export type ProfileFormType = 'FACT_FIND' | 'RISK_ATTITUDE' | 'SUSTAINABILITY' | 'DEEP_DIVE';

export interface ProfileFormTypeMeta {
  type: ProfileFormType;
  label: string;
  description: string;
  scored: boolean;
}

/** The form types the admin can configure, in display order. */
export const PROFILE_FORM_TYPES: ProfileFormTypeMeta[] = [
  {
    type: 'FACT_FIND',
    label: 'Fact Find',
    description:
      'The question set asked of clients during a fact-find. Profile-scoped (the primary client answers).',
    scored: false,
  },
  {
    type: 'RISK_ATTITUDE',
    label: 'Risk Attitude',
    description:
      'The risk-attitude questionnaire. Per-client and scored — questions, options and points are editable; ' +
      'the "Experience and Timeline" scoring formula and flag rules are fixed in code.',
    scored: true,
  },
  {
    type: 'SUSTAINABILITY',
    label: 'Sustainability',
    description:
      'The MiFID II sustainability-preferences questionnaire. Profile-scoped; questions, options and gating ' +
      '(showIf) are editable, but the preference profile + flags are computed in code (no numeric score).',
    scored: false,
  },
  {
    type: 'DEEP_DIVE',
    label: 'Deep Dive',
    description:
      'The conversational onboarding interview question bank. Profile-scoped, all free-text; per-question ' +
      '`topic` and `askWhen` hints steer the AI assistant, and the top-level `maxQuestions` caps the interview.',
    scored: false,
  },
];

export type FactFindFieldType =
  | 'text' | 'string' | 'integer' | 'number' | 'decimal'
  | 'currency' | 'boolean' | 'date' | 'enum' | 'array' | 'multiselect';

/** An enum option — a bare string (fact-find) or {value,label,points} (scored). */
export interface FactFindOption {
  value: string;
  label?: string;
  points?: number;
}

export interface FactFindItemField {
  id: string;
  label?: string;
  type: FactFindFieldType;
  required?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  scoreKey?: string;
  options?: Array<string | FactFindOption>;
}

export interface FactFindQuestion extends FactFindItemField {
  section?: string;
  itemFields?: FactFindItemField[];
}

export interface FactFindSchema {
  version?: string | number;
  title?: string;
  description?: string;
  sections?: Array<{ id: string; title: string }>;
  questions: FactFindQuestion[];
}

/** Server view of a schema row (default or override). */
export interface ProfileFormSchemaDTO {
  formType: ProfileFormType;
  schema: FactFindSchema;
  version: number;
  source: 'GLOBAL' | 'BROKERAGE';
  enabled: boolean;
}

const base = (formType: ProfileFormType) => `/api/admin/forms/${formType}/schema`;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const profileFormSchemaApi = {
  /** Global default (platform admin). */
  getDefault: (formType: ProfileFormType): Promise<ProfileFormSchemaDTO> =>
    request(`${base(formType)}/default`),
  updateDefault: (formType: ProfileFormType, schema: FactFindSchema): Promise<ProfileFormSchemaDTO> =>
    request(`${base(formType)}/default`, { method: 'PUT', body: JSON.stringify(schema) }),

  /** Per-brokerage override (brokerage admin). Returns the override if set,
   *  else the effective (global) schema tagged with its source. */
  getForBrokerage: (formType: ProfileFormType, brokerageId: string): Promise<ProfileFormSchemaDTO> =>
    request(base(formType), { headers: { 'X-Brokerage-Id': brokerageId } }),
  updateOverride: (
    formType: ProfileFormType,
    brokerageId: string,
    schema: FactFindSchema,
    enabled = true,
  ): Promise<ProfileFormSchemaDTO> =>
    request(`${base(formType)}?enabled=${enabled}`, {
      method: 'PUT',
      headers: { 'X-Brokerage-Id': brokerageId },
      body: JSON.stringify(schema),
    }),
  deleteOverride: (formType: ProfileFormType, brokerageId: string): Promise<void> =>
    request(base(formType), { method: 'DELETE', headers: { 'X-Brokerage-Id': brokerageId } }),
};

const ALLOWED_TYPES: FactFindFieldType[] = [
  'text', 'string', 'integer', 'number', 'decimal', 'currency', 'boolean', 'date', 'enum', 'array', 'multiselect',
];

export interface SchemaValidationResult {
  valid: boolean;
  errors: string[];
  questionCount: number;
}

/** Schema DSL v2 (Fact Find wizard) field types, on top of the v1 set. */
const ALLOWED_TYPES_V2: string[] = [
  ...ALLOWED_TYPES,
  'percent', 'money', 'term', 'phone', 'address', 'clientRef', 'document',
];

/**
 * Client-side structural validation of the schema JSON, mirroring the
 * Resource Server's shape rules. Authoritative validation still happens
 * server-side on save; this gives the admin fast inline feedback. Accepts
 * both the flat DSL v1 shape (non-empty "questions") and the sectioned
 * DSL v2 wizard shape (non-empty "sections" with gates/fields/groups).
 * `questionCount` reports the total field count for v2 schemas.
 */
export function validateProfileFormSchemaText(text: string): SchemaValidationResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { valid: false, errors: ['Invalid JSON: ' + (e instanceof Error ? e.message : 'parse error')], questionCount: 0 };
  }
  const obj = parsed as { questions?: unknown; sections?: unknown };
  if (Array.isArray(obj?.sections) && !Array.isArray(obj?.questions)) {
    return validateSections(obj.sections);
  }
  const errors: string[] = [];
  const questions = obj?.questions;
  if (!Array.isArray(questions) || questions.length === 0) {
    return {
      valid: false,
      errors: ['Schema must contain a non-empty "questions" array (DSL v1) or "sections" array (DSL v2)'],
      questionCount: 0,
    };
  }
  const seen = new Set<string>();
  questions.forEach((q, i) => {
    validateField(q as FactFindQuestion, `questions[${i}]`, seen, ALLOWED_TYPES, errors);
  });
  return { valid: errors.length === 0, errors, questionCount: questions.length };
}

function validateField(
  q: FactFindQuestion,
  where: string,
  seenIds: Set<string>,
  allowedTypes: string[],
  errors: string[],
): void {
  if (!q || typeof q !== 'object') { errors.push(`${where} must be an object`); return; }
  if (!q.id || typeof q.id !== 'string') errors.push(`${where} is missing a string "id"`);
  else if (seenIds.has(q.id)) errors.push(`${where} duplicate id "${q.id}"`);
  else seenIds.add(q.id);
  if (!q.type || !allowedTypes.includes(q.type)) errors.push(`${where} has invalid type "${q.type ?? ''}"`);
  if ((q.type === 'enum' || q.type === 'multiselect') && (!Array.isArray(q.options) || q.options.length === 0)) {
    errors.push(`${where} (${q.type}) needs a non-empty "options" array`);
  }
  if (q.type === 'array' && !Array.isArray(q.itemFields)) {
    errors.push(`${where} (array) needs an "itemFields" array`);
  }
}

interface SectionLike {
  id?: unknown;
  title?: unknown;
  kind?: unknown;
  gate?: { id?: unknown; type?: unknown; options?: unknown };
  fields?: unknown;
  groups?: unknown;
}

function validateSections(sections: unknown[]): SchemaValidationResult {
  const errors: string[] = [];
  let fieldCount = 0;
  if (sections.length === 0) {
    return { valid: false, errors: ['Schema must contain at least one section'], questionCount: 0 };
  }
  const sectionIds = new Set<string>();
  sections.forEach((s, i) => {
    const section = s as SectionLike;
    const where = `sections[${i}]`;
    if (!section || typeof section !== 'object') { errors.push(`${where} must be an object`); return; }
    if (!section.id || typeof section.id !== 'string') errors.push(`${where} is missing a string "id"`);
    else if (sectionIds.has(section.id)) errors.push(`${where} duplicate section id "${section.id}"`);
    else sectionIds.add(section.id);
    if (!section.title || typeof section.title !== 'string') errors.push(`${where} is missing a string "title"`);

    // Each section gets its own id namespace (mirrors the server validator).
    const seenIds = new Set<string>();

    if (section.gate) {
      const gate = section.gate;
      if (!gate.id || typeof gate.id !== 'string') errors.push(`${where}.gate is missing a string "id"`);
      if (gate.type !== 'boolean' && gate.type !== 'multiselect') {
        errors.push(`${where}.gate type must be "boolean" or "multiselect"`);
      }
      if (gate.type === 'multiselect' && (!Array.isArray(gate.options) || gate.options.length === 0)) {
        errors.push(`${where}.gate (multiselect) needs a non-empty "options" array`);
      }
    }

    if (Array.isArray(section.fields)) {
      section.fields.forEach((f, fi) => {
        validateField(f as FactFindQuestion, `${where}.fields[${fi}]`, seenIds, ALLOWED_TYPES_V2, errors);
        fieldCount++;
      });
    }
    if (Array.isArray(section.groups)) {
      (section.groups as Array<{ id?: unknown; fields?: unknown }>).forEach((g, gi) => {
        const gwhere = `${where}.groups[${gi}]`;
        if (!g || typeof g !== 'object') { errors.push(`${gwhere} must be an object`); return; }
        if (!g.id || typeof g.id !== 'string') errors.push(`${gwhere} is missing a string "id"`);
        if (!Array.isArray(g.fields) || g.fields.length === 0) {
          errors.push(`${gwhere} needs a non-empty "fields" array`);
          return;
        }
        const groupIds = new Set<string>();
        g.fields.forEach((f, fi) => {
          validateField(f as FactFindQuestion, `${gwhere}.fields[${fi}]`, groupIds, ALLOWED_TYPES_V2, errors);
          fieldCount++;
        });
      });
    }
    if (section.kind !== 'CHAT' && !Array.isArray(section.fields) && !Array.isArray(section.groups)) {
      errors.push(`${where} needs "fields" and/or "groups" (or kind: "CHAT")`);
    }
  });
  return { valid: errors.length === 0, errors, questionCount: fieldCount };
}
